import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, InjectionToken, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { routes } from './app.routes';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { provideToastr } from 'ngx-toastr';

// icons
import { TablerIconsModule } from 'angular-tabler-icons';
import * as TablerIcons from 'angular-tabler-icons/icons';

// perfect scrollbar
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxPermissionsModule } from 'ngx-permissions';
//Import all material modules
import { MaterialModule } from './material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

// code view
import { provideHighlightOptions } from 'ngx-highlightjs';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { provideNativeDateAdapter } from '@angular/material/core';
import { providePrimeNG } from 'primeng/config';
import { MyPreset } from './app.mypresetprimeng';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';

// lang
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
// import localeEs from '@angular/common/locales/es';
import localeEsCO from '@angular/common/locales/es-CO';
import Rollbar from 'rollbar';
import { environment } from 'src/environments/environment.dev';

export const RollbarService = new InjectionToken<Rollbar>('rollbar');

export function rollbarFactory(): Rollbar {
    return new Rollbar(rollbarConfig);
}

export class RollbarErrorHandler implements ErrorHandler {
    constructor(private rollbar: Rollbar) {}

    handleError(err: any): void {
        // this.rollbar.error(err.originalError || err);
        // console.error(err);
    }
}

registerLocaleData(localeEsCO);

const rollbarConfig: Rollbar.Configuration = {
    accessToken: environment.rollbarToken,
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: environment.production ? 'production' : 'development'
};

export function HttpLoaderFactory(http: HttpClient): any {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: RollbarService, useFactory: rollbarFactory },
        {
            provide: ErrorHandler,
            useFactory: (rollbar: Rollbar) => new RollbarErrorHandler(rollbar),
            deps: [RollbarService]
        },
        { provide: LOCALE_ID, useValue: 'es-CO' },
        provideNativeDateAdapter(),
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        provideAnimationsAsync(), // required animations providers
        // Tope global de toasts: por defecto ngx-toastr los apila sin límite y una ráfaga
        // (ej. mensajes de chat) llega a tapar la pantalla completa. maxOpened + autoDismiss
        // mantiene visibles solo los últimos y va soltando la cola.
        provideToastr({
            maxOpened: 4,
            autoDismiss: true,
            preventDuplicates: true,
            countDuplicates: true
        }), // Toastr providers
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideHighlightOptions({
            coreLibraryLoader: () => import('highlight.js/lib/core'),
            lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'), // Optional, add line numbers if needed
            languages: {
                typescript: () => import('highlight.js/lib/languages/typescript'),
                css: () => import('highlight.js/lib/languages/css'),
                xml: () => import('highlight.js/lib/languages/xml')
            }
        }),
        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled'
            }),
            withComponentInputBinding()
        ),
        provideHttpClient(withInterceptorsFromDi()),
        provideClientHydration(),
        // provideAnimationsAsync() ya está declarado arriba; repetirlo no aporta nada.
        importProvidersFrom(
            FormsModule,
            // ToastrModule.forRoot() se quitó: duplicaba lo que ya aporta provideToastr()
            // más arriba (son la misma configuración, una en API de módulo y otra
            // standalone). Registrar Toastr dos veces reinstala sus providers y puede
            // dejar más de un contenedor de toasts en el DOM.
            ReactiveFormsModule,
            MaterialModule,
            NgxPermissionsModule.forRoot(),
            TablerIconsModule.pick(TablerIcons),
            NgScrollbarModule,
            CalendarModule.forRoot({
                provide: DateAdapter,
                useFactory: adapterFactory
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient]
                }
            })
        ),
        providePrimeNG({
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: '.app-dark'
                }
            },
            translation: {
                accept: 'Aceptar',
                reject: 'Rechazar',
                choose: 'Elegir',
                dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
                dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
                dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
                monthNames: [
                    'Enero',
                    'Febrero',
                    'Marzo',
                    'Abril',
                    'Mayo',
                    'Junio',
                    'Julio',
                    'Agosto',
                    'Septiembre',
                    'Octubre',
                    'Noviembre',
                    'Diciembre'
                ],
                monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
                clear: 'Limpiar',
                today: 'Hoy',
                weekHeader: 'Sem',
                firstDayOfWeek: 1,
                dateFormat: 'dd/mm/yy',
                emptyMessage: 'No hay datos para mostrar',
                emptyFilterMessage: 'No se encontraron resultados'
            }
        })
    ]
};
